import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Tones map 1:1 to the Figma "Component 1009" pill variants used across the
 * publishing portal (release states, listing states, upload slot tags).
 */
export type StatusPillTone =
  | 'live'
  | 'published'
  | 'review'
  | 'unpublished'
  | 'waiting'
  | 'rejected'
  | 'suspended'
  | 'processing'
  | 'neutral'
  | 'required'
  | 'optional'
  | 'default';

const TONE_CLASSES: Record<StatusPillTone, string> = {
  live: 'bg-green-900/30 text-green-400',
  published: 'bg-green-900/30 text-green-400',
  review: 'bg-amber-900/30 text-amber-400',
  unpublished: 'bg-amber-900/30 text-amber-400',
  required: 'bg-amber-900/30 text-amber-400',
  waiting: 'bg-blue-900/30 text-blue-400',
  rejected: 'bg-rose-900/30 text-rose-400',
  suspended: 'bg-orange-900/30 text-orange-400',
  processing: 'bg-seeker-800 text-seeker-300',
  neutral: 'bg-neutral-800 text-seeker-400',
  optional: 'bg-seeker-800 text-seeker-400',
  default: 'bg-seeker-700 text-seeker-300',
};

const DOT_TONES: ReadonlySet<StatusPillTone> = new Set(['live', 'published']);

interface StatusPillProps {
  tone: StatusPillTone;
  /** 16px leading glyph. Live/published tones default to a dot. */
  icon?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
  children: ReactNode;
}

export function StatusDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center',
        className,
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
    </span>
  );
}

export function StatusPill({
  tone,
  icon,
  size = 'sm',
  className,
  children,
}: StatusPillProps) {
  const leading = icon ?? (DOT_TONES.has(tone) ? <StatusDot /> : null);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full pl-1.5 pr-2 font-normal transition-colors duration-150 [&>svg]:size-4 [&>svg]:shrink-0',
        size === 'sm' ? 'h-5 text-xs leading-4' : 'h-6 text-sm leading-5',
        !leading && 'pl-2',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {leading}
      {children}
    </span>
  );
}
